import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import { SELECTED_SCHOOL_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/School/Hooks/useSchoolsWithParams'
import {
  useCreateSingleSchoolMutation,
  useUpdateSingleSchoolMutation
} from 'Services/ControlDataManagement/Features/School/schoolApi'
import type { School } from 'Services/ControlDataManagement/Features/School/types'
import { useGetSchoolCalendarsQuery } from 'Services/ControlDataManagement/Features/SchoolCalendar/schoolCalendarApi'
import type { ControlDataServiceError, ControlDataServiceViolation } from 'Util/ApiClient'
import { formatViolationErrorField } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { Box, Grid } from '@mui/system'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  school_id: [],
  school_unit_id: [],
  name: [],
  calendar_id: []
}

interface SchoolFormProps {
  successBaseUrl: string
  school?: School
}

export default function SchoolForm({ school, successBaseUrl }: Readonly<SchoolFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!school
  const [searchParams] = useSearchParams()
  const selectedSchoolId = searchParams.get(SELECTED_SCHOOL_PARAMETER_KEY)

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const [createSingleSchool, createStatus] = useCreateSingleSchoolMutation()
  const [updateSingleSchool, updateStatus] = useUpdateSingleSchoolMutation()
  const {
    data: { resources: schoolCalendars } = { resources: [] },
    isFetching: isSchoolCalendarsFetching
  } = useGetSchoolCalendarsQuery({ bypass_pagination: true })

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [schoolDetails, setSchoolDetails] = useState<Partial<School>>(
    (isEdit && school) || {
      school_id: selectedSchoolId ?? ''
    }
  )

  const handleChange = (value: string | undefined, field: keyof School) => {
    if (value !== undefined) {
      setSchoolDetails(prevState => ({ ...prevState, [field]: value }) as School)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const mutation = isEdit
      ? updateSingleSchool({ id: school.id, ...schoolDetails })
      : createSingleSchool(schoolDetails)

    mutation
      .unwrap()
      .then((schoolResponse: School) => {
        SnackbarUtils.success(t`School successfully saved.`)
        if (isEdit) {
          navigate(successBaseUrl)
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_SCHOOL_PARAMETER_KEY]: schoolResponse.id
            })
          )
        }
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { violations, message } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error saving the school.`)
        }
      })
  }

  const schoolCalendarOptions = schoolCalendars.map(calendar => ({
    label: `${calendar.calendar_id} - ${calendar.name}`,
    value: calendar.calendar_id
  }))
  // Add empty option for school calendar
  schoolCalendarOptions.unshift({
    label: '-',
    value: ''
  })

  function setValidationErrors(violations: ControlDataServiceViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  return (
    <WithLoader loading={isSchoolCalendarsFetching}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box>
                <TextField
                  name="school_id"
                  required
                  label={t`School identifier`}
                  size="small"
                  value={schoolDetails?.school_id ?? ''}
                  slotProps={{ htmlInput: { maxLength: 40 } }}
                  onChange={e => handleChange(e.target.value, 'school_id')}
                  sx={{ minWidth: 200 }}
                  error={fieldErrors.school_id.length > 0}
                  helperText={fieldErrors.school_id.length > 0 ? fieldErrors.school_id[0] : ''}
                />
              </Box>
              <Box>
                <TextField
                  name="school_unit_id"
                  required
                  label={t`School unit`}
                  size="small"
                  value={schoolDetails?.school_unit_id ?? ''}
                  slotProps={{ htmlInput: { maxLength: 40 } }}
                  onChange={e => handleChange(e.target.value, 'school_unit_id')}
                  disabled={isEdit}
                  sx={{ minWidth: 200 }}
                  error={fieldErrors.school_unit_id.length > 0}
                  helperText={
                    fieldErrors.school_unit_id.length > 0 ? fieldErrors.school_unit_id[0] : ''
                  }
                />
              </Box>
            </Box>
            <Box>
              <TextField
                name="name"
                required
                label={t`Name`}
                size="small"
                value={schoolDetails?.name ?? ''}
                onChange={e => handleChange(e.target.value, 'name')}
                sx={{ minWidth: 200 }}
                error={fieldErrors.name.length > 0}
                helperText={fieldErrors.name.length > 0 ? fieldErrors.name[0] : ''}
              />
            </Box>
            <Box>
              <FormControl sx={{ width: 200 }} size="small" required>
                <InputLabel>{t`Calendar`}</InputLabel>
                <Select
                  name="calendar_id"
                  size="small"
                  required
                  label={t`Calendar`}
                  value={schoolDetails.calendar_id ?? ''}
                  onChange={e => {
                    handleChange(e.target.value as string, 'calendar_id')
                  }}
                  error={fieldErrors.calendar_id.length > 0}
                >
                  {schoolCalendarOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.calendar_id.length > 0}>
                  {fieldErrors.calendar_id.length > 0 ? fieldErrors.calendar_id[0] : ''}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box>
              <CustomLoadingButton
                type="submit"
                size="large"
                iconPosition="end"
                isLoading={isSubmitLoading}
              >{t`Save school`}</CustomLoadingButton>
            </Box>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
